import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserApplications as getSupabaseUserApplications, getJobApplications as getSupabaseJobApplications } from '../lib/supabase/applications';
import { getUserApplications as getFirebaseUserApplications } from '../lib/firebase/applications';
import type { JobApplication } from '../types/applications';

export function useUserApplications() {
  const { user, firebaseUser } = useAuth();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadApplications = async () => {
    const currentUser = user || firebaseUser;
    if (!currentUser) {
      setApplications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get applications from both Supabase and Firebase
      const [supabaseResult, firebaseResult] = await Promise.all([
        user ? getSupabaseUserApplications() : Promise.resolve({ applications: [], error: null }),
        firebaseUser ? getFirebaseUserApplications(firebaseUser.uid) : Promise.resolve({ applications: [], error: null })
      ]);
      
      // Combine applications from both sources
      const allApplications = [
        ...supabaseResult.applications,
        ...firebaseResult.applications.map(app => ({
          ...app,
          applicantId: app.farmhandId,
          updatedAt: app.createdAt
        }))
      ];
      
      if (supabaseResult.error && firebaseResult.error) {
        throw new Error('Failed to load applications from both systems');
      }
      
      setApplications(allApplications);
    } catch (err) {
      console.error('Error loading user applications:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
  }, [user, firebaseUser]);

  return { 
    applications, 
    loading, 
    error,
    refreshApplications: loadApplications
  };
}

export function useJobApplications(jobId: string) {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine if this is a Supabase or Firebase job
      const isSupabaseJob = jobId.includes('-') && jobId.length > 20;
      
      if (isSupabaseJob) {
        // Load from Supabase job_applications table
        const { applications: jobApps, error: appsError } = await getSupabaseJobApplications(jobId);
        
        if (appsError) {
          throw appsError;
        }
        
        setApplications(jobApps);
      } else {
        // Load from Firebase subcollection
        const { getJobApplications: getFirebaseJobApplications } = await import('../lib/firebase/applications');
        const { applications: jobApps, error: appsError } = await getFirebaseJobApplications(jobId);
        
        if (appsError) {
          throw appsError;
        }
        
        // Convert Firebase applications to standard format
        const convertedApps = jobApps.map(app => ({
          ...app,
          applicantId: app.farmhandId,
          updatedAt: app.createdAt
        }));
        
        setApplications(convertedApps);
      }
    } catch (err) {
      console.error('Error loading job applications:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      loadApplications();
    }
  }, [jobId]);

  return { 
    applications, 
    loading, 
    error,
    refreshApplications: loadApplications
  };
}